/**
 * Runner Policy Page — runner-specific overrides + effective policy.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card, Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import {
  useRunnerEffectivePolicy,
  useSaveRunnerOverrides,
} from '@/features/mcp/hooks/usePolicyLayers';
import type { RunnerPolicyConfig } from '@/features/mcp/hooks/usePolicyLayers';
import { RunnerPolicyForm } from './components/RunnerPolicyForm';

export function RunnerPolicyPage() {
  const { t } = useI18n();
  const { id: runnerId } = useParams<{ id: string }>();
  const { data: effective, isLoading } = useRunnerEffectivePolicy(runnerId ?? null);
  const saveOverrides = useSaveRunnerOverrides();
  const [draft, setDraft] = useState<RunnerPolicyConfig | null>(null);

  if (isLoading) {
    return (
      <div className="flex justify-center p-6">
        <Spinner />
      </div>
    );
  }

  const value = draft ?? {};

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-xl font-semibold">
          {t('admin.integrations.runnerPolicy') ?? 'Política del runner'}
        </h1>
        <p className="text-sm text-muted-foreground">
          Overrides específicos de este runner (máxima prioridad) — los campos vacíos heredan
          del perfil asignado y de la capa global.
        </p>
      </div>

      {effective && (
        <Card className="space-y-2 p-4">
          <h3 className="font-semibold">Estado actual</h3>
          <dl className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <dt className="text-muted-foreground">Perfil asignado</dt>
              <dd>{effective.profile_name ?? 'ninguno (global)'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Modo efectivo</dt>
              <dd>{effective.config.mode ?? 'default'}</dd>
            </div>
            <div className="col-span-2">
              <dt className="text-muted-foreground">Directorios permitidos (efectivos)</dt>
              <dd className="font-mono text-xs">{effective.config.allowed_paths?.join(', ') ?? '—'}</dd>
            </div>
          </dl>
        </Card>
      )}

      <RunnerPolicyForm value={value} onChange={setDraft} layer="runner" />

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => setDraft(null)} disabled={draft === null}>
          Descartar
        </Button>
        <Button
          onClick={async () => {
            if (draft !== null && runnerId) {
              await saveOverrides.mutateAsync({ runnerId, config: draft });
              setDraft(null);
            }
          }}
          disabled={draft === null || saveOverrides.isPending}
        >
          {saveOverrides.isPending ? 'Guardando…' : 'Guardar overrides'}
        </Button>
      </div>
    </div>
  );
}
